/**
 * ========================================
 * CHATBOT VIEW CONTROLLER MODULE
 * Handles UI state transitions and view management
 * ========================================
 */
(function () {
    'use strict';

    // State
    let isOpen = false;
    let isExpanded = false;
    let isViewingResults = false;
    let welcomeVideoTimeout = null;
    let productsVideoShownOnce = false;
    let productsVideoEligible = false;

    // Track welcome video play for this page load (resets on refresh)
    let sessionWelcomePlayed = false;
    const getHasPlayedWelcome = () => sessionWelcomePlayed;
    const setHasPlayedWelcome = () => { sessionWelcomePlayed = true; };

    // DOM Cache
    let DOM = null;

    // SVG Icons
    const ICONS = {
        VIEW_ANALYSIS: `
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="margin-right: 6px;">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="white"/>
            </svg>`,
        START_ANALYSIS: `
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="margin-right: 6px;">
              <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" fill="white"/>
              <path d="M20 4H16.83L15 2H9L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM20 18H4V6H8.05L9.88 4H14.12L15.95 6H20V18Z" fill="white"/>
            </svg>`,
        RETAKE_TEST: `
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="margin-right: 6px;">
             <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="white"/>
            </svg>`,
        TOAST_ERROR: `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>`,
        TOAST_SUCCESS: `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>`
    };

    const ViewController = {
        safeAutoplay: function (video, options) {
            if (!video) return;
            const opts = options || {};
            const tryUnmuteAfterStart = !!opts.tryUnmuteAfterStart;
            const startMuted = opts.startMuted !== false;
            video.dataset.chatbotStopped = '0';

            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.autoplay = true;
            video.preload = 'auto';

            // Cross-browser (Edge/Safari/Chrome): muted autoplay is the reliable baseline.
            video.muted = startMuted;
            const start = function () {
                if (!document.body.contains(video) || video.dataset.chatbotStopped === '1') return;
                const p = video.play();
                if (p && typeof p.then === 'function') {
                    p.then(function () {
                        if (!document.body.contains(video) || video.dataset.chatbotStopped === '1') return;
                        if (tryUnmuteAfterStart) {
                            try {
                                video.muted = false;
                                video.volume = 1;
                                const p2 = video.play();
                                if (p2 && typeof p2.catch === 'function') {
                                    p2.catch(function () {
                                        // If browser blocks unmuted playback, keep muted so at least video continues.
                                        video.muted = true;
                                    });
                                }
                            } catch (e) { /* ignore */ }
                        }
                    }).catch(function () {
                        // Keep silently paused if browser still blocks.
                    });
                }
            };

            if (video.readyState >= 2) start();
            else {
                const once = function () {
                    video.removeEventListener('loadeddata', once);
                    video.removeEventListener('canplay', once);
                    start();
                };
                video.addEventListener('loadeddata', once, { once: true });
                video.addEventListener('canplay', once, { once: true });
                try { video.load(); } catch (e) { /* ignore */ }
            }
        },

        init: function () {
            if (window.ChatbotUI) {
                DOM = window.ChatbotUI.getRefs();
                this.bindEvents();
            } else {
                console.error("ChatbotUI not loaded");
            }
        },

        refreshDOM: function () {
            if (window.ChatbotUI) {
                DOM = window.ChatbotUI.getRefs();
                return DOM;
            }
        },

        getRefs: function () {
            return DOM || (window.ChatbotUI && window.ChatbotUI.getRefs());
        },

        setProductsVideoShownOnce: function(val) {
            productsVideoShownOnce = !!val;
        },

        setProductsVideoEligible: function (val) {
            productsVideoEligible = !!val;
        },

        /**
         * Intro video sits below the product cards inside #chatbot-products (same rail), above the FAQ pane.
         * No static transcript paragraph — captions come from VTT on the video.
         */
        injectProductsIntroMedia: function () {
            if (!DOM) this.refreshDOM();
            if (!DOM || !DOM.productsContainer) return;
            if (DOM.productsContainer.querySelector('.chatbot-products-media-stack')) return;
            if (!productsVideoEligible || productsVideoShownOnce || !window.PRODUCTS_VIDEO_URL) return;

            productsVideoShownOnce = true;
            productsVideoEligible = false;

            var mediaStack = document.createElement('div');
            mediaStack.className = 'chatbot-products-media-stack';
            mediaStack.setAttribute('data-chatbot-products-media', '1');

            var videoWrap = document.createElement('div');
            videoWrap.className = 'products-video-section';

            var vid = document.createElement('video');
            vid.className = 'products-video-element';
            vid.playsInline = true;
            vid.setAttribute('playsinline', '');
            vid.preload = 'auto';
            vid.muted = true;
            vid.volume = 1;
            vid.autoplay = true;
            vid.controls = false;
            vid.style.visibility = 'visible';
            vid.style.opacity = '1';
            vid.style.width = '100%';
            vid.style.height = 'auto';
            vid.style.maxWidth = '100%';
            vid.style.objectFit = 'contain';
            vid.onended = function () {
                this.pause();
                this.currentTime = 0;
            };
            var src = document.createElement('source');
            src.src = window.PRODUCTS_VIDEO_URL;
            src.type = 'video/mp4';
            vid.appendChild(src);

            var captionsUrl = window.PRODUCTS_VIDEO_CAPTIONS_URL || '';
            var capTrack = null;
            if (captionsUrl) {
                capTrack = document.createElement('track');
                capTrack.kind = 'captions';
                capTrack.label = 'English';
                capTrack.srclang = 'en';
                capTrack.src = captionsUrl;
                capTrack.setAttribute('default', '');
                vid.appendChild(capTrack);
            }

            var captionEl = document.createElement('div');
            captionEl.className = 'products-video-dynamic-captions';
            captionEl.setAttribute('aria-live', 'polite');
            captionEl.setAttribute('aria-atomic', 'true');

            var updateLiveCaptions = function () {
                var text = '';
                try {
                    var tracks = vid.textTracks;
                    if (!tracks || !tracks.length) {
                        captionEl.textContent = '';
                        return;
                    }
                    for (var i = 0; i < tracks.length; i++) {
                        var tr = tracks[i];
                        if (tr.kind !== 'captions' && tr.kind !== 'subtitles') continue;
                        var cues = tr.activeCues;
                        if (!cues || !cues.length) continue;
                        for (var j = 0; j < cues.length; j++) {
                            var c = cues[j];
                            if (c && c.text) text += c.text.replace(/\s+/g, ' ').trim() + ' ';
                        }
                    }
                } catch (err) { /* ignore */ }
                captionEl.textContent = text.trim();
            };

            var captionCueListenersBound = false;
            var bindCaptionTracks = function () {
                try {
                    var tracks = vid.textTracks;
                    if (!tracks || !tracks.length) {
                        updateLiveCaptions();
                        return;
                    }
                    for (var k = 0; k < tracks.length; k++) {
                        var tr = tracks[k];
                        if (tr.kind !== 'captions' && tr.kind !== 'subtitles') continue;
                        tr.mode = 'hidden';
                        if (!captionCueListenersBound) {
                            tr.addEventListener('cuechange', updateLiveCaptions);
                        }
                    }
                    if (tracks.length) {
                        captionCueListenersBound = true;
                    }
                } catch (e) { /* ignore */ }
                updateLiveCaptions();
            };

            vid.addEventListener('timeupdate', updateLiveCaptions);
            vid.addEventListener('loadedmetadata', bindCaptionTracks);
            vid.addEventListener('canplay', bindCaptionTracks);
            if (capTrack) capTrack.addEventListener('load', bindCaptionTracks);

            videoWrap.appendChild(vid);
            videoWrap.appendChild(captionEl);

            mediaStack.appendChild(videoWrap);

            DOM.productsContainer.appendChild(mediaStack);

            this.safeAutoplay(vid, { tryUnmuteAfterStart: true, startMuted: true });
            setTimeout(bindCaptionTracks, 300);
        },

        stopWelcomeVideo: function () {
            if (!DOM) this.refreshDOM();
            if (welcomeVideoTimeout) {
                clearTimeout(welcomeVideoTimeout);
                welcomeVideoTimeout = null;
            }
            if (DOM && DOM.welcomeScreen) {
                const video = DOM.welcomeScreen.querySelector('.welcome-video-element');
                if (video) {
                    video.pause();
                    video.muted = true;
                    video.currentTime = 0;
                }
            }
        },

        /** Stop all audio and video in the chatbot (on page change, tab switch, or view switch) */
        stopAllChatbotMedia: function () {
            const box = document.getElementById('chatbot-box');
            if (!box) return;
            box.querySelectorAll('video').forEach(function (v) {
                try {
                    v.dataset.chatbotStopped = '1';
                    v.pause();
                    v.muted = true;
                    v.currentTime = 0;
                } catch (e) { /* ignore */ }
            });
            box.querySelectorAll('audio').forEach(function (a) {
                try {
                    a.pause();
                    a.currentTime = 0;
                } catch (e) { /* ignore */ }
            });
        },

        bindEvents: function () {
            if (!DOM) return;
            var self = this;
            document.addEventListener('visibilitychange', function () {
                if (document.hidden) self.stopAllChatbotMedia();
            });
            window.addEventListener('pagehide', function () { self.stopAllChatbotMedia(); });
            window.addEventListener('beforeunload', function () { self.stopAllChatbotMedia(); });
            window.addEventListener('popstate', function () { self.stopAllChatbotMedia(); });
            window.addEventListener('hashchange', function () { self.stopAllChatbotMedia(); });
        },

        toggleChatbot: function (forceState = null) {
            if (!DOM) this.refreshDOM();
            if (!DOM) return;

            isOpen = forceState !== null ? forceState : !isOpen;
            DOM.box.classList.toggle('open', isOpen);
            DOM.toggle.classList.toggle('hidden', isOpen);
            if (!isOpen) this.stopAllChatbotMedia();
            // When reopening, re-sync cart counts so the UI doesn't look stale.
            if (isOpen && window.ChatbotProducts && typeof window.ChatbotProducts.syncStorefrontCartCount === 'function') {
                // Delay slightly so product cards (DOM) are present after view toggle.
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        try { window.ChatbotProducts.syncStorefrontCartCount(); } catch (e) { /* ignore */ }
                    }, 250);
                });
            }
            return isOpen;
        },

        toggleExpand: function (forceState = null, shouldScroll = true) {
            if (!DOM) this.refreshDOM();
            // Expanded/full-width mode is intentionally disabled.
            isExpanded = false;
            if (DOM && DOM.box) DOM.box.classList.remove('expanded');
            if (DOM && DOM.expand) DOM.expand.innerHTML = '⛶';

            // CRITICAL FIX: If we are in "Guided Chat" or "Results" view, we must re-calculate the layout
            // immediately because our split logic depends on the 'expanded' class we just toggled.
            if (isViewingResults) {
                // Layout refresh only – don't stop products video (skipMediaStop = true)
                this.showGuidedChatView(true);
            }

            if (shouldScroll && DOM.messagesContainer) {
                setTimeout(() => {
                    DOM.messagesContainer.scrollTo({
                        top: DOM.messagesContainer.scrollHeight,
                        behavior: 'smooth'
                    });
                }, 350);
            }

            // RE-RENDER PRODUCTS ON EXPAND/COLLAPSE TO MATCH LAYOUT
            if (isViewingResults && window.ChatbotProducts && typeof window.ChatbotProducts.refreshDisplay === 'function') {
                window.ChatbotProducts.refreshDisplay();
            }

            return false;
        },

        showWelcomeScreen: function ({ hasRecommendations, canShowQuestionnaire, onStart, onView, onRetake }) {
            if (!DOM) this.refreshDOM();
            this.stopAllChatbotMedia();

            // Visibility: Hide everything else and show welcome
            if (DOM.contentContainer) {
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.style.display = 'flex';
                DOM.contentContainer.style.overflowY = 'hidden';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');
            }

            DOM.welcomeScreen.style.display = 'flex';
            DOM.questionnaireContainer.style.display = 'none';
            DOM.faceScanContainer.style.display = 'none';
            DOM.productsContainer.style.display = 'none';
            DOM.messagesContainer.style.display = 'none';
            DOM.chatInput.style.display = 'none';

            // Clear header progress/icons from results page
            if (window.ChatbotProducts && typeof window.ChatbotProducts.hideProducts === 'function') {
                window.ChatbotProducts.hideProducts();
            }
            const topHeaderTitle = document.querySelector('#chatbot-header-title span');
            if (topHeaderTitle) {
                topHeaderTitle.textContent = 'Codeskin';
                topHeaderTitle.style.visibility = 'visible';
            }

            // Play welcome video once from start - UNMUTED; VTT text shows in line below video (not on video)
            const welcomeVideo = DOM.welcomeScreen.querySelector('#welcome-video-player');
            const welcomeCaptionLine = DOM.welcomeScreen.querySelector('#welcome-video-captions-line');
            const welcomeCaptionsFallback = 'Welcome to CodeSkin. I am Cody, your AI assistant.';

            if (welcomeVideo) {
                var bindWelcomeCaptionsBelowVideo = function () {
                    if (!welcomeCaptionLine || welcomeVideo.dataset.chatbotWelcomeCaptionsInit === '1') return;
                    welcomeVideo.dataset.chatbotWelcomeCaptionsInit = '1';

                    var updateLiveCaptions = function () {
                        var text = '';
                        try {
                            var tracks = welcomeVideo.textTracks;
                            if (!tracks || !tracks.length) {
                                if (!window.WELCOME_VIDEO_CAPTIONS_URL) {
                                    welcomeCaptionLine.textContent = welcomeCaptionsFallback;
                                }
                                return;
                            }
                            for (var i = 0; i < tracks.length; i++) {
                                var tr = tracks[i];
                                if (tr.kind !== 'captions' && tr.kind !== 'subtitles') continue;
                                var cues = tr.activeCues;
                                if (!cues || !cues.length) continue;
                                for (var j = 0; j < cues.length; j++) {
                                    var c = cues[j];
                                    if (c && c.text) text += c.text.replace(/\s+/g, ' ').trim() + ' ';
                                }
                            }
                        } catch (err) { /* ignore */ }
                        welcomeCaptionLine.textContent = text.trim();
                    };

                    var captionCueListenersBound = false;
                    var bindCaptionTracks = function () {
                        try {
                            var tracks = welcomeVideo.textTracks;
                            if (!tracks || !tracks.length) {
                                if (!window.WELCOME_VIDEO_CAPTIONS_URL) {
                                    welcomeCaptionLine.textContent = welcomeCaptionsFallback;
                                }
                                updateLiveCaptions();
                                return;
                            }
                            if (!captionCueListenersBound) {
                                for (var k = 0; k < tracks.length; k++) {
                                    var tr = tracks[k];
                                    if (tr.kind !== 'captions' && tr.kind !== 'subtitles') continue;
                                    tr.addEventListener('cuechange', updateLiveCaptions);
                                }
                                captionCueListenersBound = true;
                            }
                            for (var k2 = 0; k2 < tracks.length; k2++) {
                                var tr2 = tracks[k2];
                                if (tr2.kind === 'captions' || tr2.kind === 'subtitles') tr2.mode = 'hidden';
                            }
                        } catch (e) { /* ignore */ }
                        updateLiveCaptions();
                    };

                    welcomeVideo.addEventListener('timeupdate', updateLiveCaptions);
                    welcomeVideo.addEventListener('loadedmetadata', bindCaptionTracks);
                    welcomeVideo.addEventListener('canplay', bindCaptionTracks);
                    welcomeVideo.querySelectorAll('track').forEach(function (t) {
                        t.addEventListener('load', bindCaptionTracks);
                    });
                    setTimeout(bindCaptionTracks, 300);
                };

                bindWelcomeCaptionsBelowVideo();

                if (!getHasPlayedWelcome()) {
                    if (welcomeVideoTimeout) clearTimeout(welcomeVideoTimeout);
                    welcomeVideoTimeout = setTimeout(() => {
                        console.log("[ViewController] Initializing welcome video playback...");

                        const attemptPlay = () => {
                            welcomeVideo.currentTime = 0;
                            welcomeVideo.onended = function () {
                                this.currentTime = 0;
                                this.pause();
                                if (welcomeCaptionLine) {
                                    welcomeCaptionLine.textContent = welcomeCaptionsFallback;
                                }
                            };
                            this.safeAutoplay(welcomeVideo, { tryUnmuteAfterStart: true, startMuted: true });
                            setHasPlayedWelcome();
                        };

                        // Ensure video is ready to play
                        if (welcomeVideo.readyState >= 3) {
                            attemptPlay();
                        } else {
                            welcomeVideo.oncanplay = () => {
                                welcomeVideo.oncanplay = null;
                                attemptPlay();
                            };
                            welcomeVideo.load();
                        }
                    }, 300);
                } else {
                    welcomeVideo.pause();
                    welcomeVideo.style.display = 'none'; // Also hide it once played to move text up
                    if (welcomeCaptionLine) {
                        welcomeCaptionLine.textContent = welcomeCaptionsFallback;
                    }
                }
            } else if (welcomeCaptionLine && !welcomeCaptionLine.textContent.trim()) {
                welcomeCaptionLine.textContent = welcomeCaptionsFallback;
            }

            // Bind clicks
            const self = this;
            if (onStart) DOM.startQuestionnaireBtn.onclick = () => { self.stopWelcomeVideo(); onStart(); };
            if (onView) DOM.viewAnalysisBtn.onclick = () => { self.stopWelcomeVideo(); onView(); };
            if (onRetake) DOM.retakeTestWelcomeBtn.onclick = () => { self.stopWelcomeVideo(); onRetake(); };

            if (hasRecommendations || !canShowQuestionnaire) {
                DOM.startQuestionnaireBtn.style.display = 'none';
                DOM.viewAnalysisBtn.style.display = 'flex';
                DOM.retakeTestWelcomeBtn.style.display = 'flex';
            } else {
                DOM.startQuestionnaireBtn.style.display = 'flex';
                DOM.viewAnalysisBtn.style.display = 'none';
                DOM.retakeTestWelcomeBtn.style.display = 'none';
            }
        },

        toggleResultsView: function (hasRecommendations) {
            if (!DOM) this.refreshDOM();

            if (!hasRecommendations && (!DOM.productsContainer || DOM.productsContainer.innerHTML.trim() === '')) {
                return;
            }

            // If we have recommendations coming but the container is empty, show spinner
            if (hasRecommendations && (!DOM.productsContainer || DOM.productsContainer.innerHTML.trim() === '')) {
                this.showSpinner('Preparing your results...', 60);
            }

            isViewingResults = true;
            const isWideScreen = window.innerWidth >= 768;

            if (DOM.contentContainer) {
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.style.display = 'flex';
                DOM.contentContainer.style.overflowY = 'hidden';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.add('show-results'); // ADD THIS
            }

            if (isExpanded && isWideScreen) {
                // Wide Screen Split View
                this.showGuidedChatView();
            } else {
                // Mobile/Compressed: Show both (Unified Split View)
                this.showGuidedChatView();
            }

            if (DOM.viewResultsHeaderBtn) DOM.viewResultsHeaderBtn.textContent = 'Back to Chat';
        },

        toggleChatView: function () {
            if (!DOM) this.refreshDOM();
            this.stopAllChatbotMedia();
            isViewingResults = false;

            if (DOM.contentContainer) {
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.style.display = 'flex';
                DOM.contentContainer.style.overflowY = 'hidden';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');
            }

            DOM.mainPanel.style.display = 'flex';
            DOM.mainPanel.style.width = '100%'; // Ensure full width
            DOM.mainPanel.style.height = '100%';
            DOM.mainPanel.style.flex = '1';
            DOM.mainPanel.style.order = '1';

            DOM.welcomeScreen.style.display = 'none';
            DOM.questionnaireContainer.style.display = 'none';
            DOM.faceScanContainer.style.display = 'none';
            DOM.messagesContainer.style.display = 'flex';
            DOM.messagesContainer.style.height = '100%';
            DOM.chatInput.style.display = 'none';
            DOM.productsContainer.style.display = 'none';

            if (DOM.viewResultsHeaderBtn) DOM.viewResultsHeaderBtn.textContent = 'View Results';

            setTimeout(() => {
                DOM.messagesContainer.scrollTo({ top: DOM.messagesContainer.scrollHeight, behavior: 'auto' });
            }, 50);
        },

        showGuidedChatView: function (skipMediaStop) {
            if (!DOM) this.refreshDOM();
            if (!skipMediaStop) this.stopAllChatbotMedia();
            isViewingResults = true;

            // Enable split view ONLY if screen is wide enough AND the widget is explicitly expanded
            const isWideEnough = window.innerWidth >= 1024;

            // Re-read expanded state directly from DOM to ensure accuracy
            const box = document.getElementById('chatbot-box');
            const isMsgExpanded = box ? box.classList.contains('expanded') : false;

            // CRITICAL FIX: Only split if we are TRULY expanded. 
            const shouldSplit = isMsgExpanded && isWideEnough;

            // 1. CONTAINER STYLE
            if (DOM.contentContainer) {
                DOM.contentContainer.style.display = 'flex';
                DOM.contentContainer.classList.add('guided-mode-active');
                DOM.contentContainer.style.background = '#ffffff';
                DOM.contentContainer.style.height = '100%';

                if (shouldSplit) {
                    // WIDE SCREEN SPLIT: Left Chat (70%), Right Products (30%)
                    DOM.contentContainer.style.flexDirection = 'row';
                    DOM.contentContainer.style.overflow = 'hidden';
                } else {
                    // Stacked layout: outer column does not scroll — products rail + main panel scroll inside
                    DOM.contentContainer.style.flexDirection = 'column';
                    DOM.contentContainer.style.overflowY = 'hidden';
                    DOM.contentContainer.style.overflowX = 'hidden';
                }
            }

            // 2. MAIN PANEL (CHAT AREA)
            if (DOM.mainPanel) {
                DOM.mainPanel.style.display = 'flex';
                DOM.mainPanel.style.background = '#ffffff';
                DOM.mainPanel.style.flexDirection = 'column';
                DOM.mainPanel.style.minHeight = '0';

                if (shouldSplit) {
                    DOM.mainPanel.style.flex = '0 0 70%';
                    DOM.mainPanel.style.width = '70%';
                    DOM.mainPanel.style.height = '100%';
                    DOM.mainPanel.style.overflowY = 'auto';
                    DOM.mainPanel.style.borderRight = '1px solid #e2e8f0';
                    DOM.mainPanel.style.order = '1'; // Left
                } else {
                    DOM.mainPanel.style.flex = '1 1 0';
                    DOM.mainPanel.style.width = '100%';
                    DOM.mainPanel.style.height = 'auto';
                    DOM.mainPanel.style.minHeight = '0';
                    DOM.mainPanel.style.overflowY = 'auto';
                    DOM.mainPanel.style.borderRight = 'none';
                    DOM.mainPanel.style.order = '2';
                    DOM.mainPanel.style.borderTop = '1px solid #f1f5f9';
                }

                DOM.mainPanel.style.zIndex = '5';

                // CRITICAL: Ensure we don't hide the children we actually need (messages)
                // Hiding other siblings
                DOM.welcomeScreen.style.display = 'none';
                DOM.questionnaireContainer.style.display = 'none';
                DOM.faceScanContainer.style.display = 'none';
            }

            // 3. PRODUCTS AREA
            if (DOM.productsContainer) {
                DOM.productsContainer.style.display = 'block';
                DOM.productsContainer.style.background = shouldSplit ? '#f8fafc' : '#ffffff';

                if (shouldSplit) {
                    DOM.productsContainer.style.width = '30%';
                    DOM.productsContainer.style.flex = '0 0 30%';
                    DOM.productsContainer.style.height = '100%';
                    DOM.productsContainer.style.overflowY = 'auto';
                    DOM.productsContainer.style.padding = '0'; // Removed gap at top
                    DOM.productsContainer.style.borderLeft = 'none';
                    DOM.productsContainer.style.order = '2'; // Right
                } else {
                    /* Stacked: video + cards = content-sized; FAQ pane takes remaining flex space */
                    DOM.productsContainer.style.width = '100%';
                    DOM.productsContainer.style.flex = '0 1 auto';
                    DOM.productsContainer.style.height = 'auto';
                    DOM.productsContainer.style.minHeight = '0';
                    DOM.productsContainer.style.maxHeight = 'none';
                    DOM.productsContainer.style.overflowY = 'auto';
                    DOM.productsContainer.style.overflowX = 'hidden';
                    DOM.productsContainer.style.padding = '0';
                    DOM.productsContainer.style.order = '1';
                    DOM.productsContainer.style.background = '#ffffff';
                    DOM.productsContainer.style.borderBottom = 'none';
                }

                const grids = DOM.productsContainer.querySelectorAll('.products-grid');
                grids.forEach(grid => {
                    if (shouldSplit) {
                        // Wide Screen Sidebar: Stack categories, but keep horizontal scroll for products inside if they don't fit
                        // Actually, we don't need to force column here as categories already stack
                        // But let's ensure the grid doesn't force a wrap that might look bad in a narrow sidebar
                        grid.style.flexWrap = 'nowrap';
                    } else {
                        // Mobile: Standard layout
                        grid.style.flexWrap = 'nowrap';
                    }
                });

                // Clear any inline styles periodically added to cards to ensure they follow CSS
                const cards = DOM.productsContainer.querySelectorAll('.product-card');
                cards.forEach(card => {
                    card.style.width = '';
                    card.style.minWidth = '';
                    card.style.maxWidth = '';
                    card.style.marginBottom = '';

                    const imgCont = card.querySelector('.product-image-container');
                    if (imgCont) imgCont.style.height = '';

                    const desc = card.querySelector('.product-description');
                    if (desc) desc.style.display = '';
                });

                // Ensure the header is visible as it contains primary action buttons (Retake, Add All, etc)
                const header = DOM.productsContainer.querySelector('.products-header');
                if (header) header.style.display = 'block';
            }

            // 4. MESSAGES AREA — FAQ / guided chat (intro video lives in #chatbot-products)
            if (DOM.messagesContainer) {
                DOM.messagesContainer.style.display = 'flex';
                DOM.messagesContainer.style.flexDirection = 'column';
                DOM.messagesContainer.style.background = '#ffffff';
                if (shouldSplit) {
                    DOM.messagesContainer.style.height = '100%';
                    DOM.messagesContainer.style.flex = '1 1 0';
                    DOM.messagesContainer.style.minHeight = '0';
                    DOM.messagesContainer.style.overflowY = 'auto';
                    DOM.messagesContainer.style.padding = '10px 12px 14px';
                } else {
                    DOM.messagesContainer.style.flex = '1 1 0';
                    DOM.messagesContainer.style.minHeight = '0';
                    DOM.messagesContainer.style.height = 'auto';
                    DOM.messagesContainer.style.overflowY = 'auto';
                    DOM.messagesContainer.style.padding = '12px 14px 16px';
                }

                // Keep intro video visible on initial products load.
                // Only hide it after actual guided FAQ chips are present.
                var hasGuidedContent = !!DOM.messagesContainer.querySelector('.quick-replies-container');
                if (hasGuidedContent) {
                    document.querySelectorAll('.chatbot-products-media-stack').forEach(function (node) {
                        var v = node.querySelector('video');
                        if (v) {
                            try {
                                v.pause();
                                v.currentTime = 0;
                                v.muted = true;
                            } catch (e) { /* ignore */ }
                        }
                        if (node && node.parentNode) node.parentNode.removeChild(node);
                    });
                    document.querySelectorAll('#chatbot-messages .products-video-section').forEach(function (node) {
                        if (node && node.parentNode) node.parentNode.removeChild(node);
                    });
                } else {
                    var existingMedia = DOM.productsContainer && DOM.productsContainer.querySelector('.chatbot-products-media-stack');
                    if (existingMedia) {
                        var ev = existingMedia.querySelector('video');
                        if (ev) {
                            this.safeAutoplay(ev, { tryUnmuteAfterStart: true, startMuted: true });
                        }
                    } else {
                        this.injectProductsIntroMedia();
                    }
                }
            }

            if (DOM.chatInput) DOM.chatInput.style.display = 'none';
            if (DOM.viewResultsHeaderBtn) DOM.viewResultsHeaderBtn.textContent = 'Back to Results';

            // SNAPPY LAYOUT REFRESH
            requestAnimationFrame(() => {
                if (shouldSplit) {
                    DOM.mainPanel.scrollTop = 0;
                } else {
                    // On mobile, the main.js handles the precise scroll to mainPanel.offsetTop
                }
            });
        },

        showFaceScan: function () {
            if (!DOM) this.refreshDOM();
            this.stopAllChatbotMedia();
            isViewingResults = false;

            // Hide other views
            DOM.welcomeScreen.style.display = 'none';
            DOM.questionnaireContainer.style.display = 'none';
            DOM.messagesContainer.style.display = 'none';
            DOM.productsContainer.style.display = 'none';
            DOM.chatInput.style.display = 'none';

            // Clear header progress/icons from results page
            if (window.ChatbotProducts && typeof window.ChatbotProducts.hideProducts === 'function') {
                window.ChatbotProducts.hideProducts();
            }
            const topHeaderTitle = document.querySelector('#chatbot-header-title span');
            if (topHeaderTitle) {
                topHeaderTitle.textContent = 'Codeskin';
                topHeaderTitle.style.visibility = 'visible';
            }

            // Show main panel and face scan
            DOM.mainPanel.style.display = 'flex';
            DOM.faceScanContainer.style.display = 'flex';

            // Reset panel layout
            if (DOM.contentContainer) {
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');
            }
            if (DOM.mainPanel) {
                DOM.mainPanel.style.width = '100%';
                DOM.mainPanel.style.flex = '1';
                DOM.mainPanel.style.height = '100%';
            }
        },

        resetViewForRetake: function () {
            if (!DOM) this.refreshDOM();
            this.stopAllChatbotMedia();
            isViewingResults = false;
            productsVideoShownOnce = false; // CRITICAL: Reset video flag for retake
            productsVideoEligible = false;
            
            if (DOM.productsContainer) {
                DOM.productsContainer.style.display = 'none';
                DOM.productsContainer.innerHTML = '';
            }

            // RESET LAYOUT from Split View
            if (DOM.contentContainer) {
                const isBoxExpanded = document.getElementById('chatbot-box')?.classList.contains('expanded');
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');

                // If expanded, ensure it takes full space correctly
                if (isBoxExpanded) {
                    DOM.contentContainer.style.width = '100%';
                    DOM.contentContainer.style.height = '100%';
                }
            }
            if (DOM.mainPanel) {
                DOM.mainPanel.style.width = '100%';
                DOM.mainPanel.style.flex = '1';
                DOM.mainPanel.style.borderRight = 'none';
                DOM.mainPanel.style.maxWidth = 'none';
                DOM.mainPanel.style.height = '100%';
                DOM.mainPanel.style.overflowY = 'hidden'; /* Sticky Nav Fix: Lock parent, scroll content */
                DOM.mainPanel.style.order = '1';
            }

            DOM.welcomeScreen.style.display = 'none';
            DOM.faceScanContainer.style.display = 'none';
            DOM.messagesContainer.style.display = 'none';
            DOM.chatInput.style.display = 'none';
            DOM.mainPanel.style.display = 'flex';
            DOM.questionnaireContainer.style.display = 'flex';

            // Clear header progress/icons from results page
            if (window.ChatbotProducts && typeof window.ChatbotProducts.hideProducts === 'function') {
                window.ChatbotProducts.hideProducts();
            }
            const topHeaderTitle = document.querySelector('#chatbot-header-title span');
            if (topHeaderTitle) {
                topHeaderTitle.textContent = 'Codeskin';
                topHeaderTitle.style.visibility = 'visible';
            }

            // Clear content but keep container
            if (DOM.questionnaireContent) DOM.questionnaireContent.innerHTML = '';

            if (DOM.viewResultsHeaderBtn) {
                DOM.viewResultsHeaderBtn.textContent = 'View Results';
                DOM.viewResultsHeaderBtn.style.display = 'none';
            }
        },

        showSpinner: function (message = 'Loading...', padding = 40) {
            return `
                <div style="text-align:center; padding: ${padding}px;">
                    <div style="width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #f97316; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto;"></div>
                    <p style="margin-top:10px; color:#64748b;">${message}</p>
                    <style>@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }</style>
                </div>`;
        },

        showProgressLoader: function (message = 'Analyzing your skin profile...') {
            const uniqueId = 'loader_' + Date.now();
            const resultsVideoUrl = window.RESULTS_VIDEO_URL || '';
            const videoHTML = resultsVideoUrl ? `
        <video playsinline style="width: 100%; max-width: 480px; height: auto; min-height: 150px; max-height: 250px; border-radius: 12px; margin-top: 20px; object-fit: contain; display: block; background: transparent;">
            <source src="${resultsVideoUrl}" type="video/mp4">
        </video>` : '';

            return `
                <div id="${uniqueId}" style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 40px 40px; text-align: center;">
                    <h3 style="font-size: 18px; color: #1e293b; margin-bottom: 24px; font-weight: 600;">${message}</h3>
                    <div style="width: 100%; max-width: 260px; background: #e2e8f0; border-radius: 20px; height: 12px; overflow: hidden; position: relative; box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);">
                        <div id="${uniqueId}_bar" class="js-progress-bar" style="height: 100%; background: linear-gradient(90deg, #f97316 0%, #ea580c 100%); border-radius: 20px; width: 0%; transition: width 0.3s ease-out;"></div>
                    </div>
                    <div style="margin-top: 12px; font-size: 16px; font-weight: 700; color: #f97316;">
                        <span id="${uniqueId}_percent" class="js-progress-text">0</span>%
                    </div>
                    <p style="margin-top: 12px; color: #94a3b8; font-size: 12px;">Please wait while we generate your personalized routine...</p>
                    <div style="flex-shrink: 0; width: 100%; max-width: 480px; height: auto; max-height: 260px; border-radius: 12px; margin-top: 10px; overflow: hidden; display: block;">
                        <video playsinline style="width: 100%; height: 100%; object-fit: contain; background: transparent;">
                            <source src="${resultsVideoUrl}" type="video/mp4">
                        </video>
                    </div>
                </div>`;
        },

        startProgressAnimation: function (container) {
            if (!container) return;
            const bar = container.querySelector('.js-progress-bar');
            const text = container.querySelector('.js-progress-text');
            const video = container.querySelector('video');

            // Attempt unmuted playback for the analysis video
            if (video) {
                video.onended = function () { this.currentTime = 0; this.pause(); };
                this.safeAutoplay(video, { tryUnmuteAfterStart: true, startMuted: true });
            }

            if (!bar) return;

            let progress = 0;
            // Clear any existing interval if we attach it to the element (optional safety)
            if (container._progressInterval) clearInterval(container._progressInterval);

            container._progressInterval = setInterval(() => {
                if (progress >= 100) {
                    clearInterval(container._progressInterval);
                } else {
                    // ENGAGING AI SIMULATION (Approx 5 Seconds Total)
                    // limit updates to be visually smooth but mathematically controlled

                    let increment = 0;

                    if (progress < 30) {
                        // Phase 1: Initial Scan (Fast) - 0.8s
                        increment = 1.5 + Math.random();
                    } else if (progress < 70) {
                        // Phase 2: Processing Data (Steady) - 2.0s
                        increment = 0.4 + (Math.random() * 0.3);
                    } else if (progress < 98) {
                        // Phase 3: Analyzing Skin (Thoughtful) - 1.2s, Phase 4: Finalizing (Anticipation) - 1.0s
                        increment = 0.05 + (Math.random() * 0.05);
                    } else {
                        // CAP at 98% until manually completed by API success
                        clearInterval(container._progressInterval);
                        return;
                    }

                    progress = Math.min(98, progress + increment);

                    // Update UI
                    if (bar) bar.style.width = progress + '%';
                    if (text) text.textContent = Math.floor(progress);
                }
            }, 50); // 50ms interval = 20 ticks per second
        },

        completeProgressAnimation: function (container, onComplete) {
            if (!container) {
                if (onComplete) onComplete();
                return;
            }

            const bar = container.querySelector('.js-progress-bar');
            const text = container.querySelector('.js-progress-text');

            if (container._progressInterval) clearInterval(container._progressInterval);

            let progress = parseInt(text ? text.textContent : '98');
            const finishInterval = setInterval(() => {
                progress += 1;
                if (bar) bar.style.width = progress + '%';
                if (text) text.textContent = Math.floor(progress);

                if (progress >= 100) {
                    clearInterval(finishInterval);
                    setTimeout(() => {
                        if (onComplete) onComplete();
                    }, 500); // Give user a moment to see the 100%
                }
            }, 20);
        },

        showError: function (targetElement, message, buttonLabel, onClick) {
            if (!targetElement) return;
            targetElement.innerHTML = `
                <div style="text-align:center; padding: 30px;">
                    <p style="color:#ef4444;">${message}</p>
                    ${buttonLabel ? `<button class="welcome-btn primary" onclick="${onClick}" style="margin: 15px auto; padding: 8px 20px;">${buttonLabel}</button>` : ''}
                </div>`;
        },

        showToast: function (message, type = 'normal') {
            if (!DOM) this.refreshDOM();
            if (!DOM.box) return;

            const existing = document.querySelector('.chatbot-toast');
            if (existing) existing.remove();

            const toast = document.createElement('div');
            toast.className = `chatbot-toast ${type}`;

            let icon = '';
            if (type === 'error') icon = ICONS.TOAST_ERROR;
            else if (type === 'success') icon = ICONS.TOAST_SUCCESS;

            toast.innerHTML = `${icon}<span>${message}</span>`;
            DOM.box.appendChild(toast);

            requestAnimationFrame(() => toast.classList.add('visible'));
            setTimeout(() => {
                toast.classList.remove('visible');
                setTimeout(() => toast.remove(), 300);
            }, 4000);
        },

        /**
         * Triggered on window resize to ensure layout matches state
         */
        updateLayoutForState: function () {
            if (!DOM) this.refreshDOM();
            if (!isOpen) return;

            // If we are viewing results or guided chat, re-trigger the split logic (layout only, don't stop video)
            if (isViewingResults) {
                this.showGuidedChatView(true);
                if (window.ChatbotProducts && typeof window.ChatbotProducts.refreshDisplay === 'function') {
                    window.ChatbotProducts.refreshDisplay();
                }
            }

            // CRITICAL: If screen becomes mobile-sized, ensure we are not "half-expanded"
            if (window.innerWidth < 1024 && isExpanded) {
                // We keep the internal state isExpanded, but the CSS will handle the mobile sizing.
                // However, we might want to force a refresh of components that depend on it.
            }
        }
    };

    window.ChatbotViewController = ViewController;

})();
